import { parseSortPartitionTrigger,
  angleModeFor, COMPASS_HEADING_ACCURACY_CUTOFF, COMPASS_POSITION_ACCURACY_CUTOFF,
  computeAcceptedRanges, computeRotationAcceptedRanges, computeTiltAcceptedRanges, computeCompassAcceptedRanges,
  computeSliderAcceptedRanges, computeSliderDefaultIncorrectGaps, COORD_READOUT_DECIMALS,
  evaluateFormulaRaw,
  parseCustomOrderTrigger, parseFitbWordTrigger, parseScaleValueTrigger, parseSliderHandleTrigger,
  SET_ANSWER_TRIGGER,
} from '@agavi/blocks';

import type { Block } from '@/types/experience';

import { getBlankIdsInOrder } from '@/components/task/blank-chip-preview';
import { stripHtmlForLabel } from '@/components/task/formula-plaintext';
import { fmtFixed, fmtLocale } from '@/components/task/number-format';
import { chipsToPlainText, cutChip, intervalSourceId, isCutCondition, joinChipGroups, joinChips, type ChipEvaluation, type ChipRoll, type ConditionChips } from '@/lib/condition-chips';

/**
 * ONE derivation of the human-readable label for a feedback state, shared by every surface that
 * shows one: the feedback panel's trigger zones, the live-test chip legend, and Flow.
 *
 * Governed by the standing rule (2026-08-03): **a chip shows the answer that triggers that
 * feedback**, and **a title reads as what happened** ("order is x, y, z", "{boat} is not
 * selected"). Before this module the three surfaces each derived their own text and drifted — one
 * GPS state read as `±25 m` in the feedback panel, `Within 25m` in the test legend, and `Correct`
 * in Flow, all at the same time. Fixing that per-block just reintroduces it on the next block, so
 * the sharing has to be structural.
 *
 * WHY THIS LIVES IN `lib/` AND NOT IN THE BLOCK COMPONENTS: five block components
 * (mcq/dropdown/keyword/fitb/ordering) already import `lib/flow-utils.ts`. If Flow reached back
 * into those components for their labels, that's a genuine import cycle. A neutral module both
 * sides can depend on is the only arrangement without one.
 *
 * WHY NOT `packages/blocks`: labels need `t` and `fmtLocale`, and that package is deliberately
 * i18n-free — it stays the home of pure evaluation. This module imports the pure interval maths it
 * needs from there.
 *
 * CONTRACT: for a targeted state, `stateChipLabel` and `targetedTitleFor` MUST return the same
 * string. Flow renders the persisted title while the panels derive live, so that identity is the
 * entire mechanism by which they agree.
 */

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Editor-shaped numeric fields are persisted as strings on every value-range block (`"25"`, not
 * `25`) — mirrors each block component's own local `num()`. */
function num(v: unknown): number {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

function isValidCoord(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

/** Block data arrives as a typed editor interface from the components and as an untyped record
 * from Flow. Taking `unknown` and normalizing here means neither side needs a cast at the call
 * site, and an interface without an index signature still satisfies it. */
function asData(data: unknown): Record<string, unknown> {
  return (data ?? {}) as Record<string, unknown>;
}

interface RangeAnswerShape { id?: string; value?: unknown; toleranceValue?: unknown; toleranceAbove?: unknown; toleranceBelow?: unknown }
interface RangeBetweenShape { id?: string; low?: unknown; high?: unknown }

/**
 * The answer fields that hold an EQUATION rather than a number, for the two sensors whose answers
 * can. `num()` returns 0 for a formula, so any accepted-range maths built on it describes a band
 * around due north — a real heading, confidently wrong. Every surface that names a correct answer
 * has to ask this first: the collapsed card, the test-bar chip legend, and Flow all route through
 * these labels, so getting it right here fixes all three at once.
 */
/**
 * Every authored answer expression, in order, LITERALS INCLUDED.
 *
 * The list every display surface actually wanted. `formulaAnswerTexts` below filters this to the
 * formula-bearing entries, which is right for asking "does this block vary?" and wrong for asking
 * "what are its answers?" — and every bar and chip used the filtered one, so an answer set of
 * `100` and `{n}` rendered as `{n}` alone. A `between` row was worse: it collapsed to whichever
 * single bound held the formula, so "between {a} and 50" lost the 50.
 */
export function answerExprTexts(data: unknown): string[] {
  const d = asData(data);
  const raw = (v: unknown) => typeof v === 'string' ? v : '';
  const ct = d.conditionType;
  const out = ct === 'between'
    ? ((d.betweenAnswers as { low?: unknown; high?: unknown }[] | undefined) ?? [])
        .flatMap(a => [raw(a.low), raw(a.high)])
    : ct === 'exact' || ct == null
      ? ((d.answers as { value?: unknown }[] | undefined) ?? []).map(a => raw(a.value))
      : [raw(d.threshold)];
  return out.filter(x => x.trim() !== '');
}

export function formulaAnswerTexts(data: unknown): string[] {
  const d = asData(data);
  const raw = (v: unknown) => typeof v === 'string' ? v : '';
  const hasChip = (v: string) => v.includes('{');
  const ct = d.conditionType;
  const out = ct === 'between'
    ? ((d.betweenAnswers as { low?: unknown; high?: unknown }[] | undefined) ?? [])
        .flatMap(a => [raw(a.low), raw(a.high)])
    : ct === 'exact' || ct == null
      ? ((d.answers as { value?: unknown }[] | undefined) ?? []).map(a => raw(a.value))
      : [raw(d.threshold)];
  return out.filter(hasChip);
}

function rangeAnswers(d: Record<string, unknown>) {
  return ((d.answers as RangeAnswerShape[] | undefined) ?? []).map(a => ({
    id: String(a.id ?? ''), value: num(a.value),
    toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow),
  }));
}
function rangeBetween(d: Record<string, unknown>) {
  return ((d.betweenAnswers as RangeBetweenShape[] | undefined) ?? []).map(a => ({
    id: String(a.id ?? ''), low: num(a.low), high: num(a.high),
  }));
}
function symmetry(d: Record<string, unknown>) {
  return d.toleranceSymmetry === 'asymmetric' ? 'asymmetric' as const : 'symmetric' as const;
}

// ─── Trigger parsing ──────────────────────────────────────────────────────────
// The `type:v1:v2` trigger encoding, shared by all three sensor blocks. Exported so the block
// editors parse triggers the same way this module labels them — two copies of this would be
// exactly the drift the module exists to prevent.

export type RangeTriggerType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'range';

export function parseRangeTrigger(trigger: string, fallback: RangeTriggerType = 'lt'): { type: RangeTriggerType; v1: string; v2: string } {
  const parts = trigger.split(':');
  const raw = parts[0] ?? fallback;
  const type = (['exact', 'lt', 'lte', 'gt', 'gte', 'range'].includes(raw) ? raw : fallback) as RangeTriggerType;
  return { type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}

// ─── GPS ──────────────────────────────────────────────────────────────────────

/**
 * Whether a distance should be written in kilometres rather than metres.
 *
 * GPS distances span three orders of magnitude — a 25 m tolerance at one end, a "wrong location
 * entirely" several km away at the other — and one unit reads badly somewhere along that range:
 * "5000 m" is a number you decode rather than picture. 1 km is the switch point, shared by every
 * surface so a chip, a title and the answer bar can never disagree about which unit they're in.
 *
 * Takes several values because a range has to pick ONE unit for both ends: "Between 500 and 5 km"
 * would be nonsense, so the larger end decides.
 */
export function gpsUseKm(...meters: number[]): boolean {
  const usable = meters.filter(n => Number.isFinite(n));
  return usable.length > 0 && Math.max(...usable) >= 1000;
}

/**
 * The number to render, in whichever unit `gpsUseKm` picked.
 *
 * Metres pass through exactly as authored — a teacher who typed a 25.5 m tolerance must not see it
 * become 26. Kilometres round to 0.1 km, because a metre-precise kilometre figure would imply
 * precision no GPS reading at that range actually has.
 */
export function gpsDistanceValue(meters: number, km: boolean, locale: string): string {
  return km ? fmtLocale(Math.round(meters / 100) / 10, locale) : fmtLocale(meters, locale);
}

/**
 * GPS's tolerance, for the student: "Tolerance: within 10 m".
 *
 * Shares the `blocks.tolerance.hint` label with every other block, but not the `±` shape — a
 * tolerance RADIUS is a distance from a point, not a band around a value, and "±10 m" would invite
 * reading it as a range. The unit is not optional here the way it is on Numerical, whose unit box
 * sits beside the input: "10" alone could be metres or kilometres, and that is the difference
 * between standing on the marker and standing down the road.
 *
 * Silent on a radius that is blank, zero or negative. Zero is not "exact" the way a zero ± is —
 * a zero-radius target cannot be reached by any GPS on earth, so it is a broken setup rather than
 * a strict one, and `toleranceInvalid` in gps-block already tells the teacher so.
 */
export function gpsToleranceHint(meters: number, t: TFn, locale: string): string | null {
  if (!Number.isFinite(meters) || meters <= 0) return null;
  return t('blocks.tolerance.hint', {
    value: gpsDistanceSentence('blocks.gps.toleranceWithin', meters, t, locale),
  });
}

/** Picks the metre or kilometre wording for a one-value distance sentence. The kilometre variant of
 * every such key is the same key with a `Km` suffix. */
function gpsDistanceSentence(baseKey: string, meters: number, t: TFn, locale: string): string {
  const km = gpsUseKm(meters);
  return t(km ? `${baseKey}Km` : baseKey, { value: gpsDistanceValue(meters, km, locale) });
}

export function gpsTriggerSummary(trigger: string, t: TFn, locale: string): string {
  const { type, v1, v2 } = parseRangeTrigger(trigger);
  const n1 = parseFloat(v1), n2 = parseFloat(v2);
  // An incomplete trigger still has to render something, and "?" is what the rest of this file uses.
  if (!Number.isFinite(n1)) return t('blocks.gps.triggerSummaryWithin', { value: '?' });
  switch (type) {
    case 'exact': return gpsDistanceSentence('blocks.gps.triggerSummaryExact', n1, t, locale);
    /*
     * Four triggers, four sentences.
     *
     * `lt`/`lte` shared one phrasing and `gt`/`gte` the other, from the original plan's examples —
     * but the segmented control offers all four as separate choices, so a teacher picking `<` and
     * a teacher picking `≤` got the identical chip and no way to tell which they had authored.
     * The same blindness is what left a catch-all unable to say whether it owned its boundary.
     */
    case 'lt':  return gpsDistanceSentence('blocks.gps.triggerSummaryUnder', n1, t, locale);
    case 'lte': return gpsDistanceSentence('blocks.gps.triggerSummaryWithin', n1, t, locale);
    case 'gt':  return gpsDistanceSentence('blocks.gps.triggerSummaryFarther', n1, t, locale);
    case 'gte': return gpsDistanceSentence('blocks.gps.triggerSummaryAtLeast', n1, t, locale);
    case 'range': {
      const km = gpsUseKm(n1, n2);
      return t(km ? 'blocks.gps.triggerSummaryRangeKm' : 'blocks.gps.triggerSummaryRange', {
        min: gpsDistanceValue(n1, km, locale),
        max: Number.isFinite(n2) ? gpsDistanceValue(n2, km, locale) : '?',
      });
    }
  }
}

/** "40.7128, -74.006", or null when no valid target is configured yet. */
/** Decimal places a GPS coordinate is shown to, everywhere it's shown: the student readout, the
 * teacher's answer bar, and the chips/titles derived here. Deliberately coarser than the 6 decimals
 * stored and evaluated against (`COORD_DECIMALS` in gps-block) — 5 is ~1.1 m, about what a phone GPS
 * actually delivers, so no surface claims precision the hardware doesn't have. Fixed decimals rather
 * than `fmtLocale`'s significant digits, which showed only 3 decimals (~90 m) for a 3-digit longitude
 * and so made the apparent precision depend on where on Earth you were.
 *
 * THE NUMBER ITSELF NOW LIVES IN `@agavi/blocks`, because "everywhere it's shown" turned out to
 * include a surface this file cannot reach: an answer reference quoting a fix is resolved down
 * there, and it was printing the stored 6+ digits while every surface up here showed 5. */
export const COORD_DISPLAY_DECIMALS = COORD_READOUT_DECIMALS;

function gpsTargetLabel(d: Record<string, unknown>, locale: string): string | null {
  const lat = num(d.targetLat), lng = num(d.targetLng);
  const blank = String(d.targetLat ?? '').trim() === '' || String(d.targetLng ?? '').trim() === '';
  return !blank && isValidCoord(lat, lng)
    ? `${fmtFixed(lat, locale, COORD_DISPLAY_DECIMALS)}, ${fmtFixed(lng, locale, COORD_DISPLAY_DECIMALS)}`
    : null;
}

/** Appends the target coordinates. Appended, never prefixed: every chip container truncates from
 * the end, and the distance is the part that distinguishes one targeted state from another, so it
 * has to be the part that survives. Returns the bare distance when there's no target yet, rather
 * than a dangling preposition.
 *
 * The preposition carries meaning here, so it isn't cosmetic: "of" is reserved for the CORRECT
 * state ("within 25 m of 50.343, 34.00" — the accepted zone belongs to that point), while every
 * other state is a distance measured FROM it ("more than 25 m from ...", "between 20 and 35 m
 * from ..."). That split is also why the catch-all reads "more than" rather than "beyond" — it has
 * to sit in the same family as the targeted `>` summaries it complements. */
function withGpsOrigin(distanceText: string, target: string | null, t: TFn, preposition: 'of' | 'from'): string {
  if (!target) return distanceText;
  const key = preposition === 'of' ? 'blocks.gps.chipOfTarget' : 'blocks.gps.chipFromTarget';
  return t(key, { condition: distanceText, target });
}

export function gpsCorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const d = asData(data);
  const tol = num(d.toleranceRadius);
  if (!(tol > 0)) return null;
  return withGpsOrigin(gpsDistanceSentence('blocks.gps.triggerSummaryWithin', tol, t, locale), gpsTargetLabel(d, locale), t, 'of');
}

export function gpsIncorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const d = asData(data);
  const tol = num(d.toleranceRadius);
  if (!(tol > 0)) return null;
  return withGpsOrigin(gpsDistanceSentence('blocks.gps.chipIncorrectBeyond', tol, t, locale), gpsTargetLabel(d, locale), t, 'from');
}

/**
 * One uncovered distance band, with its OPEN ENDS spoken and the target it is measured from.
 *
 * Two things the old prose could not say. The origin — every other GPS condition names it, and a
 * distance means nothing without the point it is a distance TO. And the boundary: a target at
 * "40 to 60" owns 40, so the leftover below stops just short of it, and "Between 0 and 40" reads
 * as owning 40 as well. Two cards claiming one distance, with nothing on screen to break the tie.
 *
 * The phrasing drops a bound the domain already implies rather than printing it: a gap starting
 * at 0 is "Less than 40 m", not "0 to under 40 m", because every distance is at least zero.
 */
export function gpsGapChipLabel(
  gap: { lo: number; hi: number; loInclusive: boolean; hiInclusive: boolean },
  data: unknown,
  t: TFn,
  locale: string,
): string {
  const d = asData(data);
  const { lo, hi, loInclusive, hiInclusive } = gap;
  const km = hi === Infinity ? gpsUseKm(lo) : gpsUseKm(lo, hi);
  const v = (n: number) => gpsDistanceValue(n, km, locale);
  const key = (base: string) => `blocks.gps.${base}${km ? 'Km' : ''}`;

  let distance: string;
  if (hi === Infinity) {
    // `triggerSummaryFarther` is "More than", which is already the strict reading.
    distance = t(key(loInclusive ? 'triggerSummaryAtLeast' : 'triggerSummaryFarther'), { value: v(lo) });
  } else if (lo <= 0) {
    // The floor is implied, so only the top bound is worth saying. "Within" is the inclusive word
    // this block already uses for a radius; "Less than" is its open twin.
    distance = t(key(hiInclusive ? 'triggerSummaryWithin' : 'triggerSummaryUnder'), { value: v(hi) });
  } else {
    // Each end says for itself whether it is owned by the band beside it. A middle gap between two
    // closed targets is open at both, but a neighbour that is itself open leaves its bound here.
    const base = loInclusive && hiInclusive ? 'triggerSummaryRange'
      : !loInclusive && !hiInclusive ? 'triggerSummaryRangeOpen'
      : loInclusive ? 'triggerSummaryRangeHiOpen'
      : 'triggerSummaryRangeLoOpen';
    distance = t(key(base), { min: v(lo), max: v(hi) });
  }
  return withGpsOrigin(distance, gpsTargetLabel(d, locale), t, 'from');
}

export function gpsTargetedChipLabel(trigger: string, data: unknown, t: TFn, locale: string): string {
  // The named triggers take no origin suffix: "No Response from 51.5, -0.1" is nonsense, and
  // Set Answer already means "within the tolerance OF that target".
  const d = asData(data);
  return withGpsOrigin(gpsTriggerSummary(trigger, t, locale), gpsTargetLabel(d, locale), t, 'from');
}

// ─── Compass ──────────────────────────────────────────────────────────────────

/*
 * `namedTriggerLabel` lived here: the one trigger that named itself rather than a value.
 *
 * It only ever held `no_response`, which is gone with optional's targeting — see
 * `targetedFiresIn`. `set_answer` was deliberately never in it: it states the authored condition,
 * exactly as graded's Correct card does, because a chip reading "Answer" tells the teacher
 * nothing they did not already know while "= 90°" is the same sentence every other chip on the
 * card speaks. Its callers substitute the correct-condition chips instead.
 */

export function compassTriggerSummary(trigger: string, t: TFn): string {
  const { type, v1, v2 } = parseRangeTrigger(trigger, 'exact');
  const val = v1 || '?';
  return type === 'range'
    ? t('blocks.compass.triggerSummaryRange', { min: val, max: v2 || '?' })
    : t('blocks.compass.triggerSummaryExact', { value: val });
}

/** Compass's accepted band, already wraparound-split by compass.ts, rendered as degrees. */
function compassAcceptedText(d: Record<string, unknown>, t: TFn, locale: string): string | null {
  const formulas = formulaAnswerTexts(d);
  if (formulas.length > 0) return formulas.join(', ');
  const ranges = computeCompassAcceptedRanges({
    conditionType: d.conditionType === 'between' ? 'between' : 'exact',
    answers: rangeAnswers(d), betweenAnswers: rangeBetween(d), toleranceSymmetry: symmetry(d),
  });
  if (ranges.length === 0) return null;
  return ranges.map(r => t('blocks.compass.triggerSummaryRange', { min: fmtLocale(r.lo, locale), max: fmtLocale(r.hi, locale) })).join(', ');
}

export function compassCorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const d = asData(data);
  return compassAcceptedText(d, t, locale);
}

export function compassIncorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const d = asData(data);
  const accepted = compassAcceptedText(d, t, locale);
  return accepted ? t('blocks.compass.chipAnythingBut', { condition: accepted }) : null;
}

export function compassTargetedChipLabel(trigger: string, _data: unknown, t: TFn, _locale: string): string {
  // A heading is absolute — there's no origin point to append, unlike GPS's distance-from-target.
  return compassTriggerSummary(trigger, t);
}

// ─── Angle ────────────────────────────────────────────────────────────────────

export function rotationTriggerSummary(trigger: string, t: TFn): string {
  const { type, v1, v2 } = parseRangeTrigger(trigger);
  const val = v1 || '?';
  switch (type) {
    case 'exact': return t('blocks.rotation.triggerSummaryExact', { value: val });
    case 'lt': return t('blocks.rotation.triggerSummaryLt', { value: val });
    case 'lte': return t('blocks.rotation.triggerSummaryLte', { value: val });
    case 'gt': return t('blocks.rotation.triggerSummaryGt', { value: val });
    case 'gte': return t('blocks.rotation.triggerSummaryGte', { value: val });
    case 'range': return t('blocks.rotation.triggerSummaryRange', { min: val, max: v2 || '?' });
  }
}

function rotationAcceptedText(d: Record<string, unknown>, t: TFn, locale: string): string | null {
  const formulas = formulaAnswerTexts(d);
  if (formulas.length > 0) return formulas.join(', ');
  const ranges = computeRotationAcceptedRanges({
    conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
    answers: rangeAnswers(d), betweenAnswers: rangeBetween(d),
    toleranceSymmetry: symmetry(d), threshold: num(d.threshold),
  });
  if (ranges.length === 0) return null;
  return ranges.map(r => t('blocks.rotation.gapRange', { min: fmtLocale(r.lo, locale), max: fmtLocale(r.hi, locale) })).join(', ');
}

export function rotationCorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const d = asData(data);
  return rotationAcceptedText(d, t, locale);
}

export function rotationIncorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const d = asData(data);
  const accepted = rotationAcceptedText(d, t, locale);
  return accepted ? t('blocks.rotation.chipAnythingBut', { condition: accepted }) : null;
}

export function rotationTargetedChipLabel(trigger: string, _data: unknown, t: TFn, _locale: string): string {
  return rotationTriggerSummary(trigger, t);
}

// ─── Tilt ─────────────────────────────────────────────────────────────────────

export function tiltTriggerSummary(trigger: string, t: TFn): string {
  const { type, v1, v2 } = parseRangeTrigger(trigger);
  const val = v1 || '?';
  switch (type) {
    case 'exact': return t('blocks.tilt.triggerSummaryExact', { value: val });
    case 'lt': return t('blocks.tilt.triggerSummaryLt', { value: val });
    case 'lte': return t('blocks.tilt.triggerSummaryLte', { value: val });
    case 'gt': return t('blocks.tilt.triggerSummaryGt', { value: val });
    case 'gte': return t('blocks.tilt.triggerSummaryGte', { value: val });
    case 'range': return t('blocks.tilt.triggerSummaryRange', { min: val, max: v2 || '?' });
  }
}

/** The accepted band(s), named. */
function tiltAcceptedText(d: Record<string, unknown>, t: TFn, locale: string): string | null {
  const formulas = formulaAnswerTexts(d);
  if (formulas.length > 0) return formulas.join(', ');
  const ranges = computeTiltAcceptedRanges({
    conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
    answers: rangeAnswers(d), betweenAnswers: rangeBetween(d),
    toleranceSymmetry: symmetry(d), threshold: num(d.threshold),
  });
  if (ranges.length === 0) return null;
  return ranges.map(r => t('blocks.tilt.gapRange', { min: fmtLocale(r.lo, locale), max: fmtLocale(r.hi, locale) })).join(', ');
}

export function tiltCorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  return tiltAcceptedText(asData(data), t, locale);
}

export function tiltIncorrectChipLabel(data: unknown, t: TFn, locale: string): string | null {
  const accepted = tiltAcceptedText(asData(data), t, locale);
  return accepted ? t('blocks.tilt.chipAnythingBut', { condition: accepted }) : null;
}

export function tiltTargetedChipLabel(trigger: string, _data: unknown, t: TFn, _locale: string): string {
  return tiltTriggerSummary(trigger, t);
}



// ─── Remaining blocks: the catch-all and the positive slot ─────────────────────
// These eight blocks already derive good ANSWER-DESCRIBING titles for their targeted states
// ("Selected cat", "Not selected Boat", "Custom Order: A, B, C"), and those titles are already
// resynced when the answer set changes — so their targeted chips have never been the problem, and
// returning null here (which falls back to the title) is the correct answer, not a gap.
//
// What WAS generic is the pair of default states: every block rendered a bare "Correct" and
// "Default Incorrect" in Flow's per-block grids. Default Incorrect is a CATCH-ALL, so per the
// standing rule its label names the COMPLEMENT rather than enumerating it — and for a
// combinatorial domain the complement can only be summarized. Six items in an Ordering block is
// 720 possible orders; "any other order" is not a shortcut, it is the accurate description.

// ─── Numerical / Slider ───────────────────────────────────────────────────────
// These two were the only blocks still deriving their own trigger labels privately, in their own
// files, while Flow read the PERSISTED title instead. The two agreed only because the same string
// was copied in at creation time — which is exactly how slider's dual-handle titles went wrong: a
// defaulted parameter wrote un-prefixed titles and Flow faithfully showed them.

/** The condition a numerical targeted state fires on — "42", "less than 10", "5 to 8". */
export function numericalTriggerSummary(trigger: string, t: TFn): string {
  const { type, v1, v2 } = parseRangeTrigger(trigger, 'exact');
  const val = v1 || '?';
  switch (type) {
    case 'exact': return t('blocks.numerical.triggerSummaryExact', { value: val });
    case 'lt': return t('blocks.numerical.triggerSummaryLt', { value: val });
    case 'lte': return t('blocks.numerical.triggerSummaryLte', { value: val });
    case 'gt': return t('blocks.numerical.triggerSummaryGt', { value: val });
    case 'gte': return t('blocks.numerical.triggerSummaryGte', { value: val });
    case 'range': return t('blocks.numerical.triggerSummaryRange', { min: val, max: v2 || '?' });
  }
}

/** A numerical unit-track targeted state names the unit that was picked, not a value. */
export function numericalUnitTargetedTitle(
  trigger: string, unitOptions: Array<{ id: string; label: string }>, t: TFn,
): string {
  const unit = unitOptions.find(u => u.id === trigger.slice('unit_'.length));
  return t('blocks.numerical.selectedUnitPrefix', { unit: unit?.label || '?' });
}

/** Either track, chosen by the trigger's own shape — the one entry point Flow and the panels share. */
export function numericalTargetedChipLabel(trigger: string, d: unknown, t: TFn): string {
  if (trigger.startsWith('unit_')) {
    const opts = (asData(d).unitOptions as Array<{ id: string; label: string }> | undefined) ?? [];
    return numericalUnitTargetedTitle(trigger, opts, t);
  }
  return numericalTriggerSummary(trigger, t);
}

/** The condition a slider targeted state fires on, before any handle prefix. */
export function sliderTriggerSummary(trigger: string, t: TFn): string {
  // Strip the handle prefix first: a dual-handle trigger is `a:exact:5`, so parsing it as a bare
  // condition would read "a" as the type and silently fall back to `exact`.
  const condition = /^[ab]:/.test(trigger) ? trigger.slice(2) : trigger;
  const { type, v1, v2 } = parseRangeTrigger(condition, 'exact');
  const val = v1 || '?';
  switch (type) {
    case 'exact': return t('blocks.slider.triggerSummaryExact', { value: val });
    case 'lt': return t('blocks.slider.triggerSummaryLt', { value: val });
    case 'lte': return t('blocks.slider.triggerSummaryLte', { value: val });
    case 'gt': return t('blocks.slider.triggerSummaryGt', { value: val });
    case 'gte': return t('blocks.slider.triggerSummaryGte', { value: val });
    case 'range': return t('blocks.slider.triggerSummaryRange', { min: val, max: v2 || '?' });
  }
}

/** With two handles, WHICH handle is part of the condition — "Handle 2: 90" is a different state
 * from "Handle 1: 90". `dualHandle` is required, never defaulted: defaulting it to false is the
 * exact bug that wrote un-prefixed titles into the database for Flow to read back. */
export function sliderTargetedTitle(trigger: string, t: TFn, dualHandle: boolean): string {
  // Set Answer spans BOTH handles, so it takes no handle prefix — see `sliderChips`, which states
  // the authored condition for it. This name is only a fallback; every surface renders the chips.
  if (trigger === SET_ANSWER_TRIGGER) return t('blocks.triggerKind.answer');
  const body = sliderTriggerSummary(trigger, t);
  if (!dualHandle) return body;
  const handle = trigger.startsWith('b:') ? 'b' : 'a';
  return `${t(handle === 'b' ? 'blocks.slider.handle2' : 'blocks.slider.handle1')}: ${body}`;
}

/** Block types whose Default Incorrect is summarized rather than enumerated, with the noun each
 * one's answers actually are. */
// Scale is deliberately absent: it has its own `case` above, so it never reaches the `default` arm
// this set gates. It still uses the same `chipAnyOther` string — a scale's answers are a finite
// enumerated set, so "any other rating" is exactly what Default Incorrect catches.
const ANY_OTHER_BLOCKS = new Set(['mcq', 'dropdown', 'keyword', 'ordering', 'fitb', 'slider', 'numerical']);

/**
 * Of those, the ones whose Default Incorrect has a GENERIC catch-all string to show.
 *
 * Separate from the set above because the two gate different things: `ANY_OTHER_BLOCKS` decides
 * whether this module derives a label for the block at all (MCQ needs that, for its Correct label),
 * while this set decides whether "any other X" is the right way to say its Default Incorrect.
 *
 * MCQ is absent deliberately. Its option set is CLOSED, so the complement of the correct answer is
 * a short known list and `mcqChips` enumerates it; there is no "any other selection" a student
 * could make, which is why `blocks.mcq.chipAnyOther` was never written. Conflating the two gates is
 * what put the raw key on screen.
 *
 * Kept as a list rather than derived from the locale file on purpose: "does this string exist" and
 * "should this block use one" are different questions, and answering the second from the first
 * would make a missing translation silently change what a block says.
 */
const ANY_OTHER_LABEL_BLOCKS = new Set(['dropdown', 'keyword', 'ordering', 'fitb', 'slider', 'numerical', 'sort']);

/** Ordering's accepted answer IS enumerable and short, so it gets stated exactly — the user's own
 * example of a good title ("order is x, y, z"). Reads the canonical item order straight from the
 * block, so it tracks a reorder without a resync. */
function orderingCorrectChipLabel(d: Record<string, unknown>, t: TFn): string | null {
  const items = (d.items as { text?: unknown; formula?: unknown }[] | undefined) ?? [];
  const sequence = items
    .map(i => stripHtmlForLabel(String(i.text ?? i.formula ?? '')).trim())
    .filter(Boolean)
    .join(', ');
  return sequence ? t('blocks.ordering.chipCorrectOrder', { sequence }) : null;
}


// ─── Sort ─────────────────────────────────────────────────────────────────────
// HERE, not in the block, and that is the whole rule: Flow renders a state's label through this
// module, and `lib/` cannot import a component. A derivation that lives beside the editor is a
// derivation Flow cannot reach, so Flow falls back to a stored title and the two drift — which is
// the exact failure (one GPS state rendering three ways at once) this file was created to end.

interface SortItemShape { id?: string; text?: unknown; formula?: unknown; media?: unknown }
interface SortBinShape { id?: string; label?: unknown }

/** An item or a bin by what it SAYS, falling back to its position. A blank rich-text field
 *  serialises to `<p></p>`, so the fallback tests the stripped text, not the raw markup. */
function sortMemberLabel(list: { id?: string }[], id: string, text: unknown, t: TFn, numbered: string): string {
  const index = list.findIndex(x => x.id === id);
  return stripHtmlForLabel(String(text ?? '')).trim() || t(numbered, { number: index + 1 });
}

/**
 * An item by what it SAYS — and in equations mode that is its RAW formula, not an evaluated value.
 *
 * Same reasoning as `orderingItemLabelText`: this label backs titles and chips that persist across
 * rolls, so baking one roll's number into them would make a stored title disagree with the next
 * draw. A media item never has a formula, so its `text` stays the label whatever the mode says —
 * otherwise every media item's label went blank the instant the toggle flipped.
 */
export function sortItemLabelText(
  items: SortItemShape[], id: string, t: TFn, itemsAsEquations?: boolean,
): string {
  const item = items.find(i => i.id === id);
  const raw = itemsAsEquations && item && !item.media ? item.formula : item?.text;
  return sortMemberLabel(items, id, raw, t, 'blocks.sort.itemNumbered');
}

export function sortBinLabelText(bins: SortBinShape[], id: string, t: TFn): string {
  return sortMemberLabel(bins, id, bins.find(b => b.id === id)?.label, t, 'blocks.sort.binNumbered');
}

const SORT_STRUCTURAL_LABELS: Record<string, string> = {
  all_in_one_bin: 'blocks.sort.triggerAllInOneBin',
  bins_swapped: 'blocks.sort.triggerBinsSwapped',
  one_misfiled: 'blocks.sort.triggerOneMisfiled',
};

/**
 * A targeted state's label — READABLE AS WHAT HAPPENED, which is the standing criterion.
 *
 * A per-item one carries BOTH halves. Several states can name the same item in different bins and
 * the same bin for different items, so either half alone collides, and "Trilobite" on its own does
 * not say what went wrong with it.
 */
export function sortTargetedLabel(trigger: string, d: Record<string, unknown>, t: TFn): string | null {
  const key = SORT_STRUCTURAL_LABELS[trigger];
  if (key) return t(key);
  if (trigger.startsWith('order:')) {
    const bin = trigger.slice('order:'.length);
    const bins = (d.bins as SortBinShape[] | undefined) ?? [];
    return bin === '*'
      ? t('blocks.sort.chipWrongOrderAnyBin')
      : t('blocks.sort.chipWrongOrderInBin', { bin: sortBinLabelText(bins, bin, t) });
  }
  /* A WHOLE AUTHORED ARRANGEMENT, named bin by bin — the direct analogue of Ordering's custom
     order naming its full sequence. Membership only, which is what the trigger stores. */
  const partition = parseSortPartitionTrigger(trigger);
  if (partition) {
    const items = (d.items as SortItemShape[] | undefined) ?? [];
    const bins = (d.bins as SortBinShape[] | undefined) ?? [];
    const parts = Object.entries(partition)
      .filter(([, ids]) => ids.length > 0)
      .map(([binId, ids]) => t('blocks.sort.chipItemsInBin', {
        items: ids.map(id => sortItemLabelText(items, id, t, !!d.itemsAsEquations)).join(', '),
        bin: sortBinLabelText(bins, binId, t),
      }));
    return parts.length
      ? t('blocks.sort.chipCustomArrangement', { arrangement: parts.join(t('blocks.sort.chipBinJoin')) })
      : null;
  }

  if (!trigger.startsWith('item:')) return null;
  const rest = trigger.slice('item:'.length);
  const cut = rest.lastIndexOf(':');
  if (cut <= 0) return null;
  const items = (d.items as SortItemShape[] | undefined) ?? [];
  const bins = (d.bins as SortBinShape[] | undefined) ?? [];
  return t('blocks.sort.chipItemInBin', {
    item: sortItemLabelText(items, rest.slice(0, cut), t, !!d.itemsAsEquations),
    bin: sortBinLabelText(bins, rest.slice(cut + 1), t),
  });
}

/**
 * Sort's accepted answer IS enumerable, unlike Ordering's — the key names which items go in which
 * bin, where an order is one of n! arrangements. So it is stated exactly, bin by bin.
 *
 * Read straight off the block, so it tracks an edit without a resync. A key that has not placed
 * every item returns null: there is no accepted answer to name yet, and a partial one would read
 * as the whole.
 */
export function sortCorrectChipLabel(d: Record<string, unknown>, t: TFn): string | null {
  const items = (d.items as SortItemShape[] | undefined) ?? [];
  const bins = (d.bins as SortBinShape[] | undefined) ?? [];
  const placements = ((d.key as { placements?: Record<string, string[]> } | undefined)?.placements) ?? {};
  const placed = new Set(Object.values(placements).flat());
  if (items.length === 0 || items.some(i => !i.id || !placed.has(i.id))) return null;
  const parts = bins
    .map(bin => ({ bin, ids: placements[String(bin.id)] ?? [] }))
    .filter(({ ids }) => ids.length > 0)
    .map(({ bin, ids }) => t('blocks.sort.chipItemsInBin', {
      items: ids.map(id => sortItemLabelText(items, id, t, !!d.itemsAsEquations)).join(', '),
      bin: sortBinLabelText(bins, String(bin.id), t),
    }));
  return parts.length ? parts.join(t('blocks.sort.chipBinJoin')) : null;
}

// ─── Scale ────────────────────────────────────────────────────────────────────
// One ordinal axis, one answer. This is the single derivation: the block component's own
// `triggerSummary` calls `scaleTriggerLabel` too, which is what keeps the feedback panel's trigger
// chip, the persisted targeted title, the test legend and Flow all saying one thing.

interface ScaleValueShape { value: number; label?: string; role: 'correct' | 'neutral' | 'incorrect' }

function scaleValues(d: Record<string, unknown>): ScaleValueShape[] {
  return (d.values as ScaleValueShape[] | undefined) ?? [];
}

/**
 * A point's display name: the number, with its label in parentheses when it has one — `3 (Neutral)`,
 * or plain `3` when unlabelled.
 *
 * Number first, deliberately. The label used to REPLACE the number, so on a fully-labelled Likert a
 * teacher authoring feedback saw "Neutral" with no idea it was position 3 of 5 — and the number is
 * what a student's answer actually records, so it is the part that must never be the bit that gets
 * dropped when a chip truncates. An empty label stays empty: nothing is invented to fill it.
 */
export function scaleValueLabel(value: number, values: ScaleValueShape[], t: TFn): string {
  // Labels are rich text, and this feeds chips, feedback titles and Flow — all plain-string
  // surfaces. stripHtmlForLabel is the shared flattener the other blocks already use for exactly
  // this: it also substitutes a formula's raw source rather than dropping it silently.
  const label = stripHtmlForLabel(values.find(v => v.value === value)?.label ?? '').trim();
  return label ? t('blocks.scale.valueWithLabel', { value, label }) : String(value);
}

/**
 * The label and evaluation for a Scale targeted trigger, which always names one specific point.
 * Returns null when the trigger names a point the axis no longer has, which is the caller's cue to
 * fall back.
 */
export function scaleTriggerLabel(
  trigger: string, values: ScaleValueShape[], t: TFn,
): { label: string; evaluation: 'correct' | 'incorrect' | 'neutral' } | null {
  const value = parseScaleValueTrigger(trigger);
  if (value !== null) {
    const point = values.find(v => v.value === value);
    if (!point) return null;
    return {
      label: scaleValueLabel(value, values, t),
      evaluation: point.role === 'incorrect' ? 'incorrect' : 'neutral',
    };
  }
  return null;
}

function scaleTargetedChipLabel(trigger: string, d: unknown, t: TFn): string | null {
  return scaleTriggerLabel(trigger, scaleValues(asData(d)), t)?.label ?? null;
}

/**
 * The answer key, stated exactly — Ordering's precedent, and possible here for the same reason: the
 * accepted answers are a short enumerated set, not a range or free text. Several points may be
 * marked correct (the band this block exists for), so they are joined with "or".
 *
 * Returns null when nothing is marked correct — `setup-incomplete` already reports that, and naming
 * an empty key would be worse than saying nothing.
 */
export function scaleCorrectChipLabel(d: unknown, t: TFn): string | null {
  const values = scaleValues(asData(d));
  const correct = values.filter(v => v.role === 'correct');
  if (correct.length === 0) return null;
  return correct.map(v => scaleValueLabel(v.value, values, t)).join(` ${t('blocks.connectors.or')} `);
}

/** The retry/gate states — No Answer and the sensor accuracy gates. They still get a label rather
 * than nothing: returning null made them render as an icon with EMPTY text in the test legend,
 * where the category heading says "NO ANSWER" but the chip beside it was blank.
 *
 * No Answer is a fixed condition with nothing to parameterize. The accuracy gates DO have a rule
 * worth stating — the cutoff they fire above — and it's the same string the feedback panel's own
 * locked trigger label already shows, so this brings the third surface in line rather than
 * inventing wording. */
function gateChipLabel(block: Block, trigger: string, t: TFn, locale: string): string | null {
  if (trigger === 'unanswered') return t('blocks.feedbackDefaults.noAnswerTitle');
  if (trigger === 'incomplete') return t('blocks.feedbackDefaults.incompleteAnswerTitle');
  const d = asData(block.data);
  if (trigger === 'poor_accuracy') {
    // GPS's cutoff is teacher-chosen (the Site Conditions preset), so it's read from the block.
    const cutoff = num(d.accuracyCutoff);
    return cutoff > 0 ? t('blocks.gps.feedbackTriggerPoorAccuracy', { cutoff: fmtLocale(cutoff, locale) }) : null;
  }
  // Compass's two gates are fixed constants, not teacher-configurable (see compass.ts).
  if (trigger === 'poor_heading_accuracy') {
    return t('blocks.compass.feedbackTriggerPoorHeadingAccuracy', { cutoff: fmtLocale(COMPASS_HEADING_ACCURACY_CUTOFF, locale) });
  }
  if (trigger === 'poor_position_accuracy') {
    return t('blocks.compass.feedbackTriggerPoorPositionAccuracy', { cutoff: fmtLocale(COMPASS_POSITION_ACCURACY_CUTOFF, locale) });
  }
  return null;
}


/** The Correct chip for an OPTION-based block: the option(s) actually marked correct. Several can be
 * correct at once (MCQ synonyms, Keyword alternatives), so they're joined rather than picking one —
 * the chip has to describe the whole accepted set, not a sample of it. */
function optionCorrectChipLabel(d: Record<string, unknown>, t: TFn): string | null {
  const options = (d.options as { role?: string; text?: unknown }[] | undefined) ?? [];
  const correct = options
    .filter(o => o.role === 'correct')
    .map(o => stripHtmlForLabel(String(o.text ?? '')).trim())
    .filter(Boolean);
  return correct.length ? t('blocks.mcq.chipCorrectIs', { answer: correct.join(', ') }) : null;
}

/* ── Option-based targeted titles (MCQ, Dropdown, Keyword) ───────────────────
 *
 * Moved here from the three components. Their targeted title was mechanically derived — each block
 * has a `resync*TargetedTitles` that writes it into the state on every relevant edit — but it was
 * derived INSIDE the component, so the only way Flow could read it was out of storage. Correct
 * while the resync has run, stale the moment it has not, which is precisely the drift the scale arm
 * already names ("deriving is right even for a block whose resync has not run since a row was
 * reordered"). One implementation, called by the block to write the title and by Flow to read it.
 */

interface Optionish { id?: string; text?: unknown; formula?: unknown; media?: unknown }

function optionsOf(d: Record<string, unknown>): Optionish[] {
  return (d.options as Optionish[] | undefined) ?? [];
}

/**
 * An MCQ option's label: its text (or formula in equations mode), bracketed for a media option
 * because this feeds plain-text surfaces with no room for an icon, and never empty — a label-less
 * option used to render "Selected " with nothing after it.
 */
export function mcqOptionLabelText(option: Optionish, index: number, t: TFn, optionsAsEquations?: boolean): string {
  const raw = optionsAsEquations && !option.media ? option.formula : option.text;
  const label = raw ? stripHtmlForLabel(String(raw)) : '';
  const bracketed = label && option.media ? `[${label}]` : label;
  return bracketed || t('blocks.options.optionLabel', { number: index + 1 });
}

export function mcqTargetedTitleText(option: Optionish, index: number, isAbsent: boolean, t: TFn, optionsAsEquations?: boolean): string {
  const label = mcqOptionLabelText(option, index, t, optionsAsEquations);
  return isAbsent ? t('blocks.mcq.notSelectedTitle', { label }) : `${t('blocks.answers.selectedPrefix')}${label}`;
}

/** Shows the raw formula, not an evaluated number: this is an authoring-facing label, so "which
 *  formula" is more use to a teacher than one arbitrary sample-resolved value. */
export function dropdownOptionLabelText(option: Optionish, optionsAsEquations?: boolean): string {
  if (optionsAsEquations) return String(option.formula ?? '');
  return option.text ? stripHtmlForLabel(String(option.text)) : '';
}

export function keywordOptionLabelText(option: Optionish): string {
  return String(option.text ?? '');
}

/** The targeted label for whichever of the three this block is, or null when the trigger names an
 *  option that is gone — in which case the caller keeps whatever it already had. */
function optionTargetedChipLabel(type: string, trigger: string, d: Record<string, unknown>, t: TFn): string | null {
  if (!trigger.startsWith('option_')) return null;
  const isAbsent = trigger.endsWith('_absent');
  const id = isAbsent ? trigger.slice('option_'.length, -'_absent'.length) : trigger.slice('option_'.length);
  const options = optionsOf(d);
  const idx = options.findIndex(o => o.id === id);
  if (idx === -1) return null;
  const option = options[idx]!;
  const asEquations = d.optionsAsEquations === true;
  if (type === 'mcq') return mcqTargetedTitleText(option, idx, isAbsent, t, asEquations);
  const label = type === 'dropdown' ? dropdownOptionLabelText(option, asEquations) : keywordOptionLabelText(option);
  return `${t('blocks.answers.selectedPrefix')}${label}`;
}

/* ── Ordering and FITB targeted titles ───────────────────────────────────────
 *
 * The last two off the stored title. Both needed a helper of their own moved with them: Ordering's
 * custom-order title embeds the WHOLE item sequence, and FITB's numbering is the blanks in QUESTION
 * order, which is derived from the question HTML and never stored (so it cannot go stale across an
 * add, a remove or a reorder — and so it has to be recomputed here rather than read).
 */

interface Itemish { id?: string; text?: unknown; formula?: unknown; media?: unknown }

/** An Ordering item's label. A media item never has a formula, so its `text` is the label whatever
 *  equations mode says — otherwise this went blank the instant the toggle flipped. */
export function orderingItemLabelText(item: Itemish, index: number, t: TFn, itemsAsEquations?: boolean): string {
  const raw = itemsAsEquations && !item.media ? item.formula : item.text;
  const label = raw ? stripHtmlForLabel(String(raw)) : '';
  const bracketed = label && item.media ? `[${label}]` : label;
  return bracketed || t('blocks.ordering.itemFallbackLabel', { number: index + 1 });
}

/** The comma-joined sequence a custom-order trigger names. */
export function orderingCustomOrderSequenceText(trigger: string, items: Itemish[], t: TFn, itemsAsEquations?: boolean): string {
  const order = parseCustomOrderTrigger(trigger) ?? items.map(i => String(i.id ?? ''));
  return order.map((id, i) => {
    const item = items.find(it => it.id === id);
    return item ? orderingItemLabelText(item, i, t, itemsAsEquations) : t('blocks.ordering.itemFallbackLabel', { number: i + 1 });
  }).join(', ');
}

export function orderingTargetedTitleText(trigger: string, items: Itemish[], t: TFn, itemsAsEquations?: boolean): string {
  if (trigger === 'reversed') return t('blocks.ordering.triggerTypeReversed');
  if (trigger === 'adjacent_swap') return t('blocks.ordering.triggerTypeAdjacentSwap');
  if (trigger === 'off_by_one') return t('blocks.ordering.triggerTypeOffByOne');
  return t('blocks.ordering.customOrderTitle', { sequence: orderingCustomOrderSequenceText(trigger, items, t, itemsAsEquations) });
}

function orderingTargetedChipLabel(trigger: string, d: Record<string, unknown>, t: TFn): string | null {
  const items = (d.items as Itemish[] | undefined) ?? [];
  return orderingTargetedTitleText(trigger, items, t, d.itemsAsEquations === true);
}

interface Wordish { id?: string; text?: unknown }
interface Blankish { id?: string; words?: Wordish[] }

export function fitbWordLabelText(word: Wordish, wordIdx: number, t: TFn): string {
  return String(word.text || '') || t('blocks.fitb.wordLabel', { number: wordIdx + 1 });
}

export function fitbTargetedTitleText(blankIdx: number, word: Wordish, wordIdx: number, t: TFn): string {
  return t('blocks.fitb.triggerOptionLabel', { ordinal: blankIdx + 1, word: fitbWordLabelText(word, wordIdx, t) });
}

/** Null where the trigger names a blank or word that is gone, or where the question has no blanks
 *  to number against — the caller then keeps whatever it already had. */
function fitbTargetedChipLabel(trigger: string, d: Record<string, unknown>, t: TFn): string | null {
  const parsed = parseFitbWordTrigger(trigger);
  if (!parsed) return null;
  const blanks = (d.blanks as Blankish[] | undefined) ?? [];
  const idsInOrder = getBlankIdsInOrder(typeof d.question === 'string' ? d.question : '');
  const ordered = idsInOrder.map(id => blanks.find(b => b.id === id)).filter((b): b is Blankish => !!b);
  const blankIdx = ordered.findIndex(b => b.id === parsed.blankId);
  if (blankIdx === -1) return null;
  const words = ordered[blankIdx]!.words ?? [];
  const wordIdx = words.findIndex(w => w.id === parsed.wordId);
  if (wordIdx === -1) return null;
  return fitbTargetedTitleText(blankIdx, words[wordIdx]!, wordIdx, t);
}

/** The Correct chip for a numeric RANGE block, stated as the accepted interval(s). */
function rangeCorrectChipLabel(ranges: { lo: number; hi: number }[], t: TFn, locale: string): string | null {
  if (ranges.length === 0) return null;
  const text = ranges
    .map(r => r.lo === r.hi
      ? fmtLocale(r.lo, locale)
      : t('blocks.mcq.chipRangeBetween', { min: fmtLocale(r.lo, locale), max: fmtLocale(r.hi, locale) }))
    .join(', ');
  return t('blocks.mcq.chipCorrectIs', { answer: text });
}

/**
 * SLIDER's chips, for every surface.
 *
 * The first block ported onto the chip-list master, and the reason the port was worth doing: slider
 * had three separate derivations for one fact — the panel built a string, the test bar built its
 * own, the collapsed card built a third, and Flow computed a fourth here. Three of the bugs
 * reported during its audit were those copies disagreeing (three phrasings of one answer, a chip
 * reading "Slider 2 is 283" when slider 2's answer was 1, and slider 2's chips missing entirely).
 *
 * Shape follows the user's own brief: "identical to numerical with two answers for dual slider, and
 * just plain numerical for a single slider." So a single slider reads exactly as numerical does, and
 * a dual slider reads as two of them.
 *
 * The LOGIC differs from numerical even though the shape matches, and the difference decides the
 * connector. Numerical's several answers are ALTERNATIVES — a submission satisfies one of them — so
 * Correct joins with "or". Slider's two handles are CONJUNCTIVE: `evaluateSlider` fires Correct only
 * when `submissions.every(...)` holds, so Correct joins with "and". Default Incorrect is the
 * complement of that conjunction, and the complement of (A and B) is (not A or not B) — so it joins
 * with "or", and it can be stated exactly rather than summarized. That last point is why the
 * "Any other combination" catch-all I had written is gone: a numeric domain states its complement,
 * per the standing rule, and a conjunction over two numeric axes still can.
 */
export function sliderChips(
  d: Record<string, unknown>, state: LabelableState, t: TFn, locale: string,
  /**
   * The scenario to resolve variable answers against, or `null` for the resting state.
   *
   * Slider had no such parameter, so `cfgFor` fell through to `num()` — which returns 0 for a
   * formula. A dual-handle block whose answers were `{test}` and `{test2}` reported its accepted
   * bands as `0 to 0` and its Default Incorrect gaps around zero: confident, precise, and about a
   * value neither variable can take. compass and angle already guarded this with
   * `formulaAnswerTexts` (see its own note); slider was never wired to it.
   */
  roll: ChipRoll = null,
  /**
   * Whether the accepted band is SPOKEN FOR, and so carved out of what the catch-all still gets.
   *
   * Always true in graded, where Correct is checked first and is terminal. Outside graded only a
   * Set Answer target claims it — and when nothing does, subtracting it describes a hole that is
   * not there: the catch-all really does catch positions inside the band, because nothing else is
   * looking at them. Same rule, same words, as Compass's, Tilt's and GPS's.
   */
  acceptedClaimed = true,
): ConditionChips | null {
  const dual = asData(d).dualHandle === true;
  const fmt = (n: number) => fmtLocale(n, locale);
  /** This handle's authored answer expressions, literals included — `answerExprTexts`, not the
   *  filtered `formulaAnswerTexts`, so a literal sitting beside a variable is not dropped. */
  const exprsFor = (handle: 'a' | 'b') =>
    answerExprTexts(handle === 'b' ? asData(asData(d).handleB) : asData(d));
  /** Whether this handle's answers vary at all. Only then is there no literal band to state. */
  const varyingFor = (handle: 'a' | 'b') => exprsFor(handle).some(e => e.includes('{'));
  /** Each answer in ITS OWN angle mode — a slider answer is a formula field like any other, and
   *  this label is what a STUDENT reads, so a mode mismatch here states the wrong band. */
  const resolveWith = (v: unknown, fieldId = '') => roll === null
    ? num(v)
    : evaluateFormulaRaw(String(v ?? ''), roll, angleModeFor(asData(d), fieldId));
  /** One handle's config, in the shape the evaluator wants. Handle B keeps its own condition type
   *  and tolerance, so it cannot be read off the block's top level. */
  const cfgFor = (handle: 'a' | 'b') => {
    const src = handle === 'b' ? asData(asData(d).handleB) : asData(d);
    /** As in `handleRangeConfigFrom`: handle B prefixes its single named fields. */
    const thresholdField = handle === 'b' ? 'handleB.threshold' : 'threshold';
    // Answer VALUES resolve through the roll; tolerances stay `num`, because a tolerance never
    // accepts an equation (percentage mode already covers "scales with the answer"), so resolving
    // one would claim a capability the editor does not offer.
    //
    // BLANK rows are dropped first, and that is not tidying. `num('')` is 0, a perfectly
    // resolvable number, so an untouched answer row became a real accepted band at zero — which is
    // why a slider with no answer authored advertised "0" as its correct answer. The evaluator's
    // own unresolvable-answer filter cannot catch it either, for the same reason: 0 resolves.
    const authored = (v: unknown) => String(v ?? '').trim() !== '';
    return {
      conditionType: (src.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
      answers: ((src.answers as RangeAnswerShape[] | undefined) ?? [])
        .filter(a => authored(a.value))
        .map(a => ({
          id: String(a.id ?? ''), value: resolveWith(a.value, `${a.id}.value`),
          toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow),
        })),
      betweenAnswers: ((src.betweenAnswers as RangeBetweenShape[] | undefined) ?? [])
        // Both bounds, because a half-filled row is dropped by the evaluator too — see
        // `incompleteBetweenRowIds`, which is what tells the teacher about it.
        .filter(a => authored(a.low) && authored(a.high))
        .map(a => ({
          id: String(a.id ?? ''), low: resolveWith(a.low, String(a.id ?? '')), high: resolveWith(a.high, String(a.id ?? '')),
        })),
      toleranceMode: (src.toleranceMode as 'absolute' | 'percentage') ?? 'absolute',
      toleranceSymmetry: symmetry(src), threshold: resolveWith(src.threshold, thresholdField),
    };
  };
  const handles: ('a' | 'b')[] = dual ? ['a', 'b'] : ['a'];
  /** "Slider 1: <text>" when there are two to tell apart, the bare text when there is only one —
   *  the same "don't announce something already obvious" rule the rest of this block follows. */
  const prefixed = (handle: 'a' | 'b', text: string) => dual
    ? t('blocks.slider.handleSummaryLabel', { n: handle === 'b' ? 2 : 1, value: text })
    : text;

  if (state.type === 'targeted') {
    /*
     * SET ANSWER STATES THE AUTHORED CONDITION, and says so here rather than at each call site.
     *
     * It carries no value of its own — it points at Correct — so the trigger summary produced an
     * operator with its operand missing ("Slider 1: < ?"), and the handle prefix was wrong on top
     * of that: a joint card is about BOTH handles, and `set_answer` carries no `a:`/`b:` for
     * `sliderTargetedTitle` to read, so it defaulted to Slider 1.
     *
     * Deciding it INSIDE the derivation is what makes four surfaces agree for free — the test
     * bar, the collapsed feedback card, the expanded trigger editor and Flow all arrive here.
     * Compass, GPS and Tilt each branch at their own call sites instead, which is why Flow still
     * names their Set Answer cards "= ?".
     */
    if (state.trigger === SET_ANSWER_TRIGGER) {
      return sliderChips(d, { ...state, type: 'default', trigger: 'correct' }, t, locale, roll);
    }
    const label = sliderTargetedTitle(state.trigger, t, dual);
    return label ? [{ kind: 'chip', label, evaluation: 'incorrect' }] : null;
  }

  if (state.trigger === 'correct') {
    const per = handles.map(h => {
      // An equation answer with no scenario has no band to state — it moves with the dice — so it
      // states the equation, as chips. Same rule compass and angle follow.
      //
      // ONE CHIP PER ANSWER, joined "or": a handle's several answers are alternatives. A single
      // chip carrying every formula was the collapsed reading the other blocks also had, and it
      // hides how many answers there actually are.
      if (varyingFor(h) && roll === null) {
        const fx = exprsFor(h);
        if (fx.length === 0) return null;
        // The handle prefix goes on the rendered LABEL too, not only on the flattened `text`. It
        // was on `text` alone, so the legend (which resolves and takes the prefixed path) said
        // "Slider 1: 98.94 to 105.06" while the panel beside it showed a bare `{test}` chip with
        // no way to tell which slider it belonged to.
        const tag = dual ? `${t(h === 'b' ? 'blocks.slider.handle2' : 'blocks.slider.handle1')}: ` : '';
        return joinChips(fx.map(e => ({
          kind: 'chip' as const, label: tag, formulas: [e], text: prefixed(h, e),
          evaluation: 'correct' as const,
        })), 'or');
      }
      // A one-sided condition states its COMPARISON, never a band clamped to the track — see
      // `cutChip`, which is shared because three blocks each got this differently wrong. Slider's
      // version was the harshest: the finite-bounds filter below dropped the cut entirely and the
      // feedback card showed no correct answer at all.
      const cfg = cfgFor(h);
      if (isCutCondition(cfg.conditionType)) {
        const src = h === 'b' ? asData(asData(d).handleB) : asData(d);
        return cutChip(cfg.conditionType, String(src.threshold ?? ''), cfg.threshold,
          fmt, 'correct', h) ?? null;
      }

      const ranges = computeSliderAcceptedRanges(cfg)
        .filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi));
      if (ranges.length === 0) return null;
      // ONE CHIP PER BAND, each carrying the interval it covers — so the legend lights only the
      // band a submission landed in. Joined text would light for every band at once.
      const per = ranges.map(r => ({
        kind: 'chip' as const,
        /*
         * A POINT STATES ITS OPERATOR; a band states its bounds.
         *
         * A zero-tolerance answer used to render as a bare "10" here while the targeted card
         * beside it said "= 10" for the identical condition. Two spellings of one fact, and the
         * bare one is the worse of them twice over: it sits in a row that can also hold "< 10"
         * and "10 to 20", where a number with no operator reads as one that failed to render,
         * and on a slider the student DRAGS to a position, so "10" invites "about 10" when the
         * answer admits nothing but 10. Tilt and Rotation say it the same way.
         */
        label: prefixed(h, r.lo === r.hi ? t('blocks.slider.chipPoint', { value: fmt(r.lo) })
          : t('blocks.slider.gapRange', { min: fmt(r.lo), max: fmt(r.hi) })),
        evaluation: 'correct' as const,
        sourceId: intervalSourceId(h, r.lo, r.hi),
      }));
      return joinChips(per, 'or');
    }).filter((c): c is NonNullable<typeof c> => c !== null)
      // A handle may now contribute SEVERAL chips (one per answer), so each is its own group and
      // the groups are joined rather than the chips.
      .map(c => Array.isArray(c) ? c : [c]);
    return per.length === 0 ? null : joinChipGroups(per, 'and');
  }

  if (state.trigger === 'incorrect') {
    /*
     * Targeted feedback carves territory OUT of Default Incorrect, so the gaps have to be computed
     * against it — passing an empty trigger list (which the first version did) made this claim
     * Default Incorrect covers ground a targeted state has already taken.
     *
     * Filtered PER HANDLE: a trigger is prefixed `a:` or `b:`, and slider 1's carve-out has nothing
     * to do with slider 2's territory. Reading them off the block data rather than taking them as a
     * parameter is what keeps one signature for all three surfaces — Flow has the persisted states
     * too, it just never used them.
     */
    const allTriggers = ((asData(d).feedback as { states?: unknown[] } | undefined)?.states ?? [])
      .filter((st): st is { type: string; trigger: string } =>
        !!st && typeof st === 'object' && (st as { type?: unknown }).type === 'targeted')
      .map(st => st.trigger);
    const per = handles.map(h => {
      // Numerical's form, which the user picked as the target: "not <equation>", with the equation
      // drawn as chips. The alternative this replaces was a band computed with `num()`, i.e. "0 to
      // 0" — and compass/angle's "Anything but {n}" rendered the variable as literal text.
      // One "not <answer>" per answer, joined "and" WITHIN the handle: Default Incorrect fires when
      // the answer is none of them, and the complement of a union is an intersection of exclusions.
      // Across handles it stays "or" (see the join at the end) — the complement of the conjunction
      // "every handle satisfied". Both connectors are correct and they are not the same one.
      if (varyingFor(h) && roll === null) {
        const fx = exprsFor(h);
        if (fx.length === 0) return null;
        const tagI = dual ? `${t(h === 'b' ? 'blocks.slider.handle2' : 'blocks.slider.handle1')}: ` : '';
        return joinChips(fx.map(e => ({
          kind: 'chip' as const,
          label: `${tagI}${t('blocks.answers.notWord')} `, formulas: [e],
          text: prefixed(h, `${t('blocks.answers.notWord')} ${e}`),
          evaluation: 'incorrect' as const,
        })), 'and');
      }
      // `[]` when nothing claims the band: the catch-all then genuinely catches positions inside
      // it, and subtracting it would describe a hole that is not there. See `acceptedClaimed`.
      const ranges = acceptedClaimed ? computeSliderAcceptedRanges(cfgFor(h)) : [];
      const mine = dual
        ? allTriggers.filter(trig => parseSliderHandleTrigger(trig).handle === h)
        : allTriggers;
      const gaps = computeSliderDefaultIncorrectGaps(ranges, mine)
        .filter(g => !Number.isNaN(g.lo) && !Number.isNaN(g.hi));
      if (gaps.length === 0) return null;
      /*
       * ONE CHIP PER GAP, joined "or" — not one chip whose text contains the "or".
       *
       * The gaps are what a chip is FOR on a live surface: the teacher drags the handle, and the
       * chip covering where it landed lights up. A single chip reading "< 229.89 or > 244.11" lights
       * up for both, so a teacher who wants to tell "too low" from "too high" cannot — they have to
       * work out which half of their own chip fired. Separate chips answer it by position.
       *
       * A BOUNDED gap keeps its two bounds in one chip ("> 76.22 and < 98"), and that is not the
       * same thing: it is a single interval a value is inside, so one chip is exactly one condition.
       * Only DISJOINT gaps get separate chips.
       */
      const perGap = gaps.map(g => {
        const lo = g.lo === -Infinity ? null : g.lo;
        const hi = g.hi === Infinity ? null : g.hi;
        /*
         * The BOTH-unbounded case is "every value is incorrect", and it used to fall through to `''`
         * — no text, so the chip was dropped, so the handle contributed nothing, so the whole state
         * reported itself UNREACHABLE. Exactly backwards: with no answer authored, nothing is
         * accepted, so Default Incorrect is the one state that catches everything. A block with no
         * answer told the teacher its catch-all could never fire.
         *
         * Same fault numerical had as "<= Infinity", surfacing differently because the two
         * formatters disagreed about what to do with an infinite bound.
         */
        const text = lo === null && hi === null ? t('blocks.slider.chipAnyValue')
          : lo === null && hi !== null ? t('blocks.slider.gapLt', { value: fmt(hi) })
          : hi === null && lo !== null ? t('blocks.slider.gapGt', { value: fmt(lo) })
          : lo !== null && hi !== null ? t('blocks.slider.gapRange', { min: fmt(lo), max: fmt(hi) })
          : '';
        return text ? {
          kind: 'chip' as const, label: prefixed(h, text), evaluation: 'incorrect' as const,
          sourceId: intervalSourceId(h, g.lo, g.hi),
        } : null;
      }).filter((c): c is NonNullable<typeof c> => c !== null);
      return perGap.length === 0 ? null : joinChips(perGap, 'or');
    }).filter((c): c is NonNullable<typeof c> => c !== null)
      .map(c => Array.isArray(c) ? c : [c]);
    // No gaps on any handle means nothing reaches Default Incorrect at all.
    if (per.length === 0) return [{ kind: 'unreachable' }];
    return joinChipGroups(per, 'or');
  }
  return null;
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────

/** Shape this module needs from a feedback state — structurally satisfied by both `FeedbackState`
 * (from @agavi/blocks) and Flow's own `FeedbackStateShape`, so neither caller has to convert. */
interface LabelableState {
  type: 'default' | 'targeted';
  trigger: string;
  title?: string;
}

/**
 * The canonical chip label for one state on one block, or null when the state has no meaningful
 * answer-condition to show (No Answer, the accuracy gates — their own titles already say it).
 *
 * Returns null for block types not yet ported, so a caller can fall back to the state's title
 * without special-casing which blocks are done.
 */
/**
 * THE MASTER DERIVATION, as a chip list.
 *
 * Every surface reads this: the feedback panel draws it with `ConditionChipRow`, the test-bar
 * legend with `toTestBarChips`, and Flow flattens it with `chipsToPlainText` (see `stateChipLabel`
 * just below, which is now exactly that and nothing else).
 *
 * Before this, the chip surfaces had a rich list they could not share with Flow and Flow had a
 * plain string it could not share with them, because the list type lived in `components/` where
 * `lib/` cannot reach. Each block then picked a side and wrote a second derivation for the other —
 * which is how one product ended up with three chip systems and a standing rule that could not be
 * followed. The type moved to `lib/condition-chips.ts`; this is the function that becomes possible
 * once it has.
 *
 * TODAY every block still derives a single string, so this wraps that one string in a one-chip list
 * and Flow's output is byte-identical — which `chip-derivation-lock.test.ts` asserts. The point is
 * not that it does something new yet; it is that a block being ported can now return a real list
 * (fan-outs with connectors, an unreachable pill, an MCQ media icon) and every surface, Flow
 * included, gets it without a second derivation existing anywhere.
 */
export function stateChipsFor(
  block: Block, state: LabelableState, t: TFn, locale: string,
): ConditionChips | null {
  // Ported blocks answer here, with a real list. Everything else still derives one string, which
  // the fallback below wraps — see this function's own note.
  const gate = gateChipLabel(block, state.trigger, t, locale);
  if (!gate && !modeDecidesState(block, state)) {
    if (block.type === 'slider') return sliderChips(block.data as Record<string, unknown>, state, t, locale);
  }
  const text = stateLabelText(block, state, t, locale);
  if (text === null) return null;
  return [{ kind: 'chip', label: text, evaluation: chipEvaluationFor(state) }];
}

/**
 * A state's tone, from the state itself.
 *
 * The renderers need it and a bare string could not carry it, so every caller was inferring it
 * separately from whatever it happened to know. It is a property of the state, so it belongs with
 * the state's label.
 */
function chipEvaluationFor(state: LabelableState): ChipEvaluation {
  if (state.type === 'targeted') return 'incorrect';
  if (state.trigger === 'correct' || state.trigger === 'any_answer'
      || state.trigger === 'any_response') return 'correct';
  if (state.trigger === 'incorrect') return 'incorrect';
  // No Answer and the sensor accuracy gates are neither right nor wrong — they are "we did not get
  // an answer to grade".
  return 'neutral';
}

/**
 * Flow's label: the master list, flattened.
 *
 * Kept as a named export because Flow, three block components and `flow-utils` all call it, and
 * because "the text of the chips" is a genuinely useful thing to ask for. What matters is that it
 * no longer COMPUTES anything — it renders. A fourth surface cannot introduce a fourth string.
 */
export function stateChipLabel(block: Block, state: LabelableState, t: TFn, locale: string): string | null {
  return chipsToPlainText(stateChipsFor(block, state, t, locale), {
    and: t('blocks.connectors.and'),
    or: t('blocks.connectors.or'),
    unreachable: t('blocks.feedbackPanel.unreachable'),
  });
}

/**
 * Whether the MODE, not the block, decides this state's label.
 *
 * Factored out so a PORTED block cannot bypass it. A block switched to Participation keeps its
 * stored `correct` state until something re-normalizes, and this is what stops a slider showing
 * "Slider 1: 55 to 65" as its positive chip on a mode that grades nothing. Extracted rather than
 * re-tested inside `sliderChips`, because a second copy of this rule is precisely the class of
 * duplication the port exists to remove.
 */
function modeDecidesState(block: Block, state: LabelableState): boolean {
  const gradingMode = (asData(block.data).gradingMode as string | undefined) ?? 'graded';
  if (gradingMode === 'graded' || state.type !== 'default') return false;
  // A boolean, not the label: the first version returned the string and used `''` to mean "the mode
  // decides, and the answer is nothing" for `incorrect`. Empty string is falsy, so the guard read it
  // as "the mode has no opinion" and let the ported block answer — a survey slider started showing
  // its Default Incorrect gaps, on a mode that grades nothing. The lock caught it; the sentinel was
  // the bug, so there is no sentinel now.
  return state.trigger === 'correct' || state.trigger === 'any_answer'
    || state.trigger === 'any_response' || state.trigger === 'incorrect';
}

/**
 * The accepted answer, named per block — the body of the `correct` arm, lifted so Set Answer can
 * ask the same question.
 *
 * Not reached by recursing on a `correct` state, which is the obvious move and is wrong: the
 * mode guard at the top of `stateLabelText` rewrites `correct` to "Any Answer" outside graded,
 * so a Set Answer card would have been labelled with the catch-all's name — which is exactly
 * what it is not.
 */
function correctAnswerLabel(block: Block, d: unknown, t: TFn, locale: string): string | null {
  const data = asData(d);
  switch (block.type) {
          case 'ordering': return orderingCorrectChipLabel(data, t);
          case 'sort': return sortCorrectChipLabel(data, t);
          // Option-based: name the option(s) marked correct.
          case 'mcq':
          case 'dropdown':
          case 'keyword': return optionCorrectChipLabel(data, t);
          // Range-based: state the accepted interval.
          case 'slider': return rangeCorrectChipLabel(computeSliderAcceptedRanges({
            conditionType: (data.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
            answers: rangeAnswers(data), betweenAnswers: rangeBetween(data),
            toleranceMode: (data.toleranceMode as 'absolute' | 'percentage') ?? 'absolute',
            toleranceSymmetry: symmetry(data), threshold: num(data.threshold),
          }), t, locale);
          case 'numerical': return rangeCorrectChipLabel(computeAcceptedRanges({
            conditionType: (data.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
            answers: rangeAnswers(data), betweenAnswers: rangeBetween(data),
            toleranceMode: (data.toleranceMode as 'absolute' | 'percentage') ?? 'absolute',
            toleranceSymmetry: symmetry(data), threshold: num(data.threshold),
          }), t, locale);
          // fitb answers PER BLANK, so a single Correct chip would either be a wall of text or a lie
          // by omission. Left to its own deep audit, where the right shape (one chip per blank? a
          // count?) is a real design question rather than a port. Scale had the same note until its
          // audit settled it — see `scaleCorrectChipLabel` for the shape that worked there.
          default: return null;
        }
  return null;
}

/** The per-block derivation itself, unchanged. Private: callers want chips, not this. */
function stateLabelText(block: Block, state: LabelableState, t: TFn, locale: string): string | null {
  const d = block.data;
  // Gates first: they're block-type-independent and would otherwise fall through every arm's
  // `return null` and render as a blank chip.
  const gate = gateChipLabel(block, state.trigger, t, locale);
  if (gate) return gate;

  // A correctness-derived label is only meaningful when the block actually grades. Flow reads RAW
  // persisted data, and a block can still carry a `correct` state after being switched to
  // Participation or Optional (the positive slot is renamed on normalize, not on every read) — so
  // without this a Participation block showed "Within 25 m of 40.7128, -74.006" as its positive
  // chip, describing an accuracy that mode never checks. Falling through to the stored title is the
  // honest answer there.
  const gradingMode = (asData(d).gradingMode as string | undefined) ?? 'graded';
  if (gradingMode !== 'graded' && state.type === 'default') {
    // The POSITIVE slot is named by the mode, never by the stored state. A block switched to
    // Optional keeps its graded `correct` state until something re-normalizes it, so trusting the
    // stored title showed "Correct" on a mode that has no correct answer. Deriving from the mode
    // makes the chip right regardless of which positive state happens to be persisted.
    if (state.trigger === 'correct' || state.trigger === 'any_answer' || state.trigger === 'any_response') {
      return t(gradingMode === 'optional'
        ? 'blocks.feedbackDefaults.anyResponseTitle'
        : 'blocks.feedbackDefaults.anyAnswerTitle');
    }
    // Correctness-derived text is meaningless when the block doesn't grade — fall through to the
    // stored title rather than describing an accuracy this mode never checks.
    if (state.trigger === 'incorrect') return null;
  }
  /*
   * SET ANSWER STATES THE CORRECT CONDITION, on every block that offers it.
   *
   * It carries no value of its own — it points at the accepted band — so handing it to a
   * targeted-chip derivation produced an operator with its operand missing. Flow is where that
   * showed, because Flow reaches these labels through `stateChipLabel` and has no call site of
   * its own to branch at: a pathway off a Set Answer card was named "= ?°" on Compass and Tilt,
   * and "Within ? m from 0.00000, 0.00000" on GPS.
   *
   * Four lines rather than one shared guard above the switch, because the answer is per block:
   * each case already knows how to say its own "correct", and this is that same question asked
   * by another name. Slider answers it inside `sliderChips` and so never arrives here.
   */
  switch (block.type) {
    case 'gps':
      if (state.trigger === SET_ANSWER_TRIGGER) return gpsCorrectChipLabel(d, t, locale);
      if (state.type === 'targeted') return gpsTargetedChipLabel(state.trigger, d, t, locale);
      if (state.trigger === 'correct') return gpsCorrectChipLabel(d, t, locale);
      if (state.trigger === 'incorrect') return gpsIncorrectChipLabel(d, t, locale);
      return null;
    case 'compass':
      if (state.trigger === SET_ANSWER_TRIGGER) return compassCorrectChipLabel(d, t, locale);
      if (state.type === 'targeted') return compassTargetedChipLabel(state.trigger, d, t, locale);
      if (state.trigger === 'correct') return compassCorrectChipLabel(d, t, locale);
      if (state.trigger === 'incorrect') return compassIncorrectChipLabel(d, t, locale);
      return null;
    case 'rotation':
      if (state.trigger === SET_ANSWER_TRIGGER) return rotationCorrectChipLabel(d, t, locale);
      if (state.type === 'targeted') return rotationTargetedChipLabel(state.trigger, d, t, locale);
      if (state.trigger === 'correct') return rotationCorrectChipLabel(d, t, locale);
      if (state.trigger === 'incorrect') return rotationIncorrectChipLabel(d, t, locale);
      return null;
    case 'tilt':
      if (state.trigger === SET_ANSWER_TRIGGER) return tiltCorrectChipLabel(d, t, locale);
      if (state.type === 'targeted') return tiltTargetedChipLabel(state.trigger, d, t, locale);
      if (state.trigger === 'correct') return tiltCorrectChipLabel(d, t, locale);
      if (state.trigger === 'incorrect') return tiltIncorrectChipLabel(d, t, locale);
      return null;
    case 'scale':
      // Targeted states derive live here rather than falling through to the stored title — the two
      // are the same string by construction (resyncScaleTargetedTitles writes what
      // `scaleTriggerLabel` returns), but deriving is right even for a block whose resync has not
      // run since a row was reordered.
      if (state.type === 'targeted') return scaleTargetedChipLabel(state.trigger, d, t);
      if (state.trigger === 'incorrect') return t('blocks.scale.chipAnyOther');
      if (state.trigger === 'correct') return scaleCorrectChipLabel(d, t);
      return null;
    default: {
      if (!ANY_OTHER_BLOCKS.has(block.type)) return null;
      // Numerical and Slider DERIVE their targeted labels here rather than falling through to the
      // stored title. The other five in this set genuinely should fall through — their titles name
      // an option/item that this module cannot see — but these two encode the whole condition in
      // the trigger, so deriving is strictly better: it survives a title that was written before
      // dual-handle was switched on, which is the case that actually broke.
      if (state.type === 'targeted') {
        /*
         * SET ANSWER STATES THE CORRECT CONDITION — see the sensor cases above, which say the
         * same thing for the same reason. It carries no value of its own, so the targeted
         * derivations below would parse it for a number it does not have.
         *
         * Recursing on `correct` rather than naming each block's label: this arm already
         * dispatches that question a few lines down, and a second copy of the switch is exactly
         * what this function exists to avoid.
         */
        if (state.trigger === SET_ANSWER_TRIGGER) return correctAnswerLabel(block, d, t, locale);
        if (block.type === 'numerical') return numericalTargetedChipLabel(state.trigger, d, t);
        if (block.type === 'slider') return sliderTargetedTitle(state.trigger, t, asData(d).dualHandle === true);
        // Option-based blocks derive here now too, rather than Flow reading a title the block
        // happened to have written. See the note above optionTargetedChipLabel.
        if (block.type === 'mcq' || block.type === 'dropdown' || block.type === 'keyword') {
          return optionTargetedChipLabel(block.type, state.trigger, asData(d), t);
        }
        if (block.type === 'ordering') return orderingTargetedChipLabel(state.trigger, asData(d), t);
        if (block.type === 'sort') return sortTargetedLabel(state.trigger, asData(d), t);
        if (block.type === 'fitb') return fitbTargetedChipLabel(state.trigger, asData(d), t);
        return null;
      }
      // Only the blocks that HAVE a generic catch-all string. MCQ is in `ANY_OTHER_BLOCKS` for its
      // Correct label below, but its Default Incorrect enumerates (see `mcqChips`) and
      // `blocks.mcq.chipAnyOther` was deliberately never written — so asking for it here rendered
      // the raw key in Flow, as `MCQ 1 Is blocks.mcq.chipAnyOther`. Returning null lets Flow use its
      // own `defaultIncorrectOption` ("Default Incorrect"), which is the honest label for a surface
      // that needs ONE string and so cannot enumerate a complement.
      if (state.trigger === 'incorrect') {
        return ANY_OTHER_LABEL_BLOCKS.has(block.type) ? t(`blocks.${block.type}.chipAnyOther`) : null;
      }
      if (state.trigger === 'correct') return correctAnswerLabel(block, d, t, locale);
      return null;
    }
  }
}

/**
 * The persisted title for a targeted state — identical to its chip label by contract, so Flow
 * (title-driven) and the panels (live-derived) can't disagree.
 *
 * Because this depends on data OUTSIDE the trigger (GPS's target coordinates), the title goes
 * stale when that data changes, which is why the sensor blocks now resync their titles on every
 * answer-setup edit the way mcq/dropdown/keyword/ordering/fitb/scale already did.
 */
export function targetedTitleFor(block: Block, trigger: string, t: TFn, locale: string): string {
  const label = stateChipLabel(block, { type: 'targeted', trigger }, t, locale);
  return label ?? trigger;
}

// ─── Conditional branches ────────────────────────────────────────────────────

/** The comparison that distinguishes one conditional branch from its siblings — "= 0", "< 5",
 * "2 to 8". Shown beside the "1 of 3" position in the FEEDBACK panel's branch cycler, where the
 * operator/threshold controls are not visible, so the position alone cannot tell a teacher which
 * branch they are editing. Deliberately NOT the whole equation: that is already on screen in the
 * answer setup, and with a formula it wraps and pushes the feedback content down. */
export function branchConditionLabel(
  operator: string, threshold: string, thresholdHigh?: string | null,
): string {
  const lo = threshold.trim() || '?';
  if (operator === 'between') return `${lo} – ${(thresholdHigh ?? '').trim() || '?'}`;
  const symbol = operator === '<=' ? '≤' : operator === '>=' ? '≥' : operator === '==' ? '=' : operator;
  return `${symbol} ${lo}`;
}
